import React from "react";
import PropTypes from "prop-types";
import { FT, fmt } from "./partnerPortalShared";
import { InvoiceStatusBadge } from "./StageBadge";
import { referralLogin } from "../../locales/en";

// Per-client invoice ledger drilldown. Rendered as a seamless expanded panel
// (matching the commission month/partner expansion) rather than a nested card.
// `invoices` is the list from GET /user-mgmt/partner-portal/customers/{id}/invoices.
// An archived or not-approved client with no invoices shows a blank panel: it
// is no longer onboarding, so the onboarding message does not apply.
const COLS = "0.9fr 1fr 0.8fr 0.8fr 0.8fr 0.9fr 1.2fr";
const CLOSED_STAGES = ["archived", "not_approved"];

function Panel({ children }) {
  return (
    <div
      style={{ background: FT.navy04, borderBottom: `1px solid ${FT.navy08}` }}
    >
      {children}
    </div>
  );
}

Panel.propTypes = {
  children: PropTypes.node
};

export default function InlineDrillDown({ customer, loading, invoices }) {
  if (loading) {
    return (
      <Panel>
        <div style={{ padding: "16px 20px", color: FT.navy60, fontSize: 13 }}>
          Loading invoices…
        </div>
      </Panel>
    );
  }

  if (!invoices || invoices.length === 0) {
    const isClosed = CLOSED_STAGES.includes(customer?.stage);
    return (
      <Panel>
        <div style={{ padding: "16px 20px", color: FT.navy60, fontSize: 13 }}>
          {isClosed ? null : referralLogin?.noInvoiceMessage}
        </div>
      </Panel>
    );
  }

  return (
    <Panel>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: COLS,
          columnGap: 16,
          padding: "10px 20px",
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: 0.5,
          textTransform: "uppercase",
          color: FT.navy60,
          borderBottom: `1px solid ${FT.navy08}`
        }}
      >
        <div>Invoice</div>
        <div>Status</div>
        <div>Funded</div>
        <div>Due</div>
        <div>Repaid</div>
        <div style={{ textAlign: "right" }}>Invoice $</div>
        <div style={{ textAlign: "right" }}>Commission</div>
      </div>

      {invoices.map((inv, i) => {
        const repaidShown =
          inv.lifecycleStatus === "completed" && inv.dateRepaid;
        const isPaid = inv.commissionStatus === "paid";
        const earnsCommission =
          inv.commission != null &&
          inv.commission > 0 &&
          inv.commissionStatus !== "none";
        return (
          <div
            key={inv.quoteId}
            style={{
              display: "grid",
              gridTemplateColumns: COLS,
              columnGap: 16,
              padding: "11px 20px",
              fontSize: 13,
              alignItems: "center",
              borderBottom:
                i === invoices.length - 1 ? "none" : `1px solid ${FT.navy08}`
            }}
          >
            <div style={{ fontWeight: 600, color: FT.navy }}>
              FT{inv.quoteId}
            </div>
            <div>
              <InvoiceStatusBadge status={inv.lifecycleStatus} />
            </div>
            <div
              style={{ fontVariantNumeric: "tabular-nums", color: FT.navy80 }}
            >
              {fmt.dateShort(inv.fundedDate)}
            </div>
            <div
              style={{ fontVariantNumeric: "tabular-nums", color: FT.navy80 }}
            >
              {fmt.dateShort(inv.dueDate)}
            </div>
            <div
              style={{
                fontVariantNumeric: "tabular-nums",
                color: repaidShown ? FT.navy80 : FT.navy40
              }}
            >
              {repaidShown ? fmt.dateShort(inv.dateRepaid) : "—"}
            </div>
            <div
              style={{
                textAlign: "right",
                fontVariantNumeric: "tabular-nums",
                color: FT.navy80
              }}
            >
              {inv.fundingAmount != null ? fmt.money0(inv.fundingAmount) : "—"}
            </div>
            <div style={{ textAlign: "right" }}>
              {earnsCommission ? (
                <>
                  <div
                    style={{
                      fontVariantNumeric: "tabular-nums",
                      color: FT.navy,
                      fontWeight: 700
                    }}
                  >
                    {fmt.money(inv.commission)}
                  </div>
                  {inv.payoutDate && (
                    <div
                      style={{ fontSize: 10.5, color: FT.navy60, marginTop: 1 }}
                    >
                      {isPaid ? "Paid" : "Pays"} {fmt.dateShort(inv.payoutDate)}
                    </div>
                  )}
                </>
              ) : (
                <span
                  style={{
                    color: FT.navy40,
                    fontVariantNumeric: "tabular-nums"
                  }}
                >
                  —
                </span>
              )}
            </div>
          </div>
        );
      })}
    </Panel>
  );
}

InlineDrillDown.propTypes = {
  customer: PropTypes.shape({ stage: PropTypes.string }),
  loading: PropTypes.bool,
  invoices: PropTypes.array
};
